import { subYears } from 'date-fns';
import { find, sortBy } from 'lodash-es';
import { inject, injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import { API_DATE } from '../constants/datePatterns';
import { LegalDocumentClassifications, PolicyOnAccount, PolicyValuesType } from '../constants/lookups';
import { MiddlewareInjectToken } from '../constants/tokens';
import { Apis, MiddlewareConfig } from '../types';
import {
    AgreementContentType,
    DocumentItem,
    DocumentItemWithExtraInfo,
    LegalDocumentContent,
    LegalDocumentWithExtraInfo,
} from '../types/Document.type';
import { formatDate, formatDocumentData, formatLegalDocumentData } from '../utils';

@injectable()
export class DocumentApi extends BaseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
    ) {
        super(apis, config);
    }

    private REQUEST_CONFIG = {
        responseType: 'blob' as const,
    };

    async getAccountDocuments(
        accountNumber: string,
        docType: string,
        fromDate?: Date,
        toDate?: Date,
    ): Promise<DocumentItemWithExtraInfo[]> {
        const response = await this.apis.orbis.documents.documentList({
            account: accountNumber,
            documentType: docType,
            fromMonth: fromDate ? formatDate(fromDate.toString(), { pattern: API_DATE }) : undefined,
            toMonth: toDate ? formatDate(toDate.toString(), { pattern: API_DATE }) : undefined,
        });

        return formatDocumentData(response.data.documentList as DocumentItem[]);
    }

    async getAccountDocumentsConfirms(
        accountNumber: string,
        fromDate?: Date,
        toDate?: Date,
    ): Promise<DocumentItemWithExtraInfo[]> {
        const response = await this.apis.orbis.documents.listOfConfirms({
            account: accountNumber,
            beginDate: fromDate ? formatDate(fromDate.toString(), { pattern: API_DATE }) : undefined,
            endDate: toDate ? formatDate(toDate.toString(), { pattern: API_DATE }) : undefined,
        });

        return formatDocumentData(response.data.confirm);
    }

    async getAccountDocumentsService(accountNumber: string, docType: DocumentType): Promise<DocumentItem[]> {
        const beginDate = subYears(new Date(), 10);
        const documents = (
            await this.apis.orbis.documents.serviceDocs({
                accountNumber,
                docType,
                beginMonth: formatDate(beginDate, { pattern: 'MM' }),
                beginYear: formatDate(beginDate, { pattern: 'yyyy' }),
                endMonth: formatDate(new Date(), { pattern: 'MM' }),
                endYear: formatDate(new Date(), { pattern: 'yyyy' }),
            })
        ).data?.documentList as DocumentItem[];
        return sortBy(documents, (document) => new Date(document.documentDate).getTime());
    }

    async getPolicyValues(type: PolicyValuesType): Promise<string[]> {
        const response = await this.apis.orbis.legalDocuments.policyValues(`${type}`);
        return response.data;
    }
    async updatePolicyOnAccount(
        accountId: number,
        policy: PolicyOnAccount,
        type: PolicyValuesType,
    ): Promise<{ success: boolean }> {
        const response = await this.apis.orbis.legalDocuments.updatesPolicyOnTheAccount(`${type}`, {
            account: { accountId },
            policy,
            esigned: true,
        });
        return response.data;
    }

    async getLegalDocuments(classification: LegalDocumentClassifications): Promise<LegalDocumentWithExtraInfo[]> {
        const response = await this.apis.orbis.legalDocuments.documentListing({ classification });

        if (response.data !== null && response.data.length > 0) {
            return formatLegalDocumentData(response.data);
        } else {
            return [];
        }
    }

    async getFundingAgreements(): Promise<AgreementContentType | null> {
        let response = await this.apis.orbis.legalDocuments.documentListing({
            classification: LegalDocumentClassifications.Funding,
        });

        const id = find(response.data, (list) => list.contentType === 'JSON').id;

        if (id) {
            response = await this.apis.orbis.legalDocuments.documentContent({ id });
            return response.data;
        } else {
            return null;
        }
    }

    async downloadDocument(accountNumber: string, key: string): Promise<Blob> {
        const response = await this.apis.orbis.documents.download(
            {
                account: accountNumber,
                key: key,
            },
            this.REQUEST_CONFIG,
        );

        return response.data;
    }

    async downloadLegalDocument(id: string): Promise<Blob> {
        const response = await this.apis.orbis.legalDocuments.documentDownload({ id }, this.REQUEST_CONFIG);
        return response.data;
    }

    async getLegalDocumentContent(id: string | number): Promise<LegalDocumentContent> {
        const response = await this.apis.orbis.legalDocuments.documentContent({ id });

        return response.data;
    }

    async signLegalDocument(params: {
        esigned: boolean;
        documentId: string;
        signature: string;
    }): Promise<{ success: true }> {
        const response = await this.apis.orbis.legalDocuments.signTheDocument({ ...params });
        return response.data;
    }
}
